import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabaseClient } from '../utils/supabaseClient';
import { Mail, Lock, UserPlus, ArrowLeft, MapPin } from 'lucide-react';

interface RegisterPageProps {
  onBack: () => void;
}

const RegisterPage: React.FC<RegisterPageProps> = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [tokenUsed, setTokenUsed] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');
  const [tokenChecked, setTokenChecked] = useState(false);

  // クエリパラメータからtoken取得
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const token = query.get('token');

  // トークンがない場合は無料プラン（自由登録）
  const isFreeRegistration = !token;

  useEffect(() => {
    const fetchTokenEmail = async () => {
      if (token) {
        const { data, error } = await supabaseClient
          .from('registration_tokens')
          .select('email')
          .eq('token', token)
          .single();
        if (data && data.email) {
          setTokenEmail(data.email);
        }
        setTokenChecked(true);
      }
    };
    fetchTokenEmail();
  }, [token]);

  useEffect(() => {
    const checkToken = async () => {
      if (!token) return;
      const { data, error } = await supabaseClient
        .from('registration_tokens')
        .select('used')
        .eq('token', token)
        .single();
      if (data && data.used) {
        setTokenUsed(true);
      }
    };
    checkToken();
  }, [token]);

  if (tokenUsed) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-white p-8 rounded shadow">
          <h2 className="text-xl font-bold mb-4">この登録リンクはすでに使用されています</h2>
          <button onClick={onBack} className="btn bg-[#007AFF] text-white px-4 py-2 rounded">ログイン画面に戻る</button>
        </div>
      </div>
    );
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    // 使用するメールアドレス（トークンありの場合はtokenEmail、なしの場合はemail）
    const registerEmail = isFreeRegistration ? email : tokenEmail;

    if (!registerEmail.trim() || !password.trim() || !confirmPassword.trim()) {
      setErrorMessage("全ての項目を入力してください");
      return;
    }

    // メールアドレスの形式チェック
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(registerEmail)) {
      setErrorMessage("有効なメールアドレスを入力してください");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("パスワードが一致しません");
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      // ユーザー登録
      const { data: authData, error: authError } = await supabaseClient.auth.signUp({
        email: registerEmail,
        password,
        options: {
          emailRedirectTo: `https://repligo.forceai.jp/app`,
        },
      });

      if (authError) throw authError;

      if (!authData.user) {
        throw new Error("ユーザー登録に失敗しました");
      }

      // 登録成功時のポップアップを表示
      setShowSuccessPopup(true);

      // 3秒後にログイン画面に戻る
      setTimeout(() => {
        onBack();
      }, 3000);
    } catch (error: any) {
      setErrorMessage(error.message || "登録処理中にエラーが発生しました");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100 p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-xl overflow-hidden">
          <div className="bg-[#007AFF] p-6 text-center">
            <h1 className="text-2xl font-bold text-white">
              {isFreeRegistration ? '無料アカウント登録' : 'アカウント登録'}
            </h1>
            {isFreeRegistration && (
              <p className="text-blue-100 text-sm mt-2">3コメントまで無料でAI返信を生成できます</p>
            )}
          </div>

          <form onSubmit={handleRegister} className="p-8 space-y-6">
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                メールアドレス
              </label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  value={isFreeRegistration ? email : tokenEmail}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-[#007AFF]"
                  placeholder="example@example.com"
                  readOnly={!isFreeRegistration}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                パスワード
              </label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-[#007AFF]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                パスワード（確認）
              </label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-[#007AFF]"
                />
              </div>
            </div>
            
            {errorMessage && (
              <div className="text-red-600 text-sm font-medium">
                {errorMessage}
              </div>
            )}
            
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={onBack}
                className="flex-1 flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF]"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                戻る
              </button>
              
              <button
                type="submit"
                disabled={loading}
                className="flex-1 flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#007AFF] hover:bg-[#0066CC] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    処理中...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <UserPlus className="mr-2 h-4 w-4" />
                    登録
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold mb-4">登録完了</h2>
            <p className="text-gray-600 mb-4">
              登録用のメールを送信しました。メールをご確認ください。
            </p>
            <div className="flex justify-end">
              <button
                onClick={onBack}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-[#007AFF] hover:bg-[#0066CC] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF]"
              >
                ログイン画面に戻る
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterPage; 